import intersection from 'lodash.intersection'
import React, {useEffect} from 'react'
import useSWR from 'swr'
import type {SWRResponse} from 'swr/dist/types'

import type {BookkeeperPermission, CompanyPermissions, EmployeePermission} from '@pleo-io/deimos'
import {LoadingPage} from '@pleo-io/telescope'

import {request} from '@product-web/api'
import {RequestAuth} from '@product-web/api'
import * as apiAuthUser from '@product-web/api-auth/user'
import * as apiDeimosEmployee from '@product-web/api-deimos/employee'
import {checkExternalBookkeepersEntitlement} from '@product-web/api-deimos/entitlement'
import * as apiDeimosUser from '@product-web/api-deimos/user'
import type {Address} from '@product-web/api-types/address'
import type {PlanType} from '@product-web/api-types/plan'
import type {Company} from '@product-web/api-types/user'
import {invariant} from '@product-web/error/invariant'
import {reportError} from '@product-web/error/report'
import type {SupportedLanguage} from '@product-web/i18n'
import type Country from '@product-web/locale/country'
import {isCountry} from '@product-web/locale/country'
import {countryToLocaleMap, getLocaleByLanguage} from '@product-web/locale/helpers'
import Locale from '@product-web/locale/locale'
import {useAllowedRoles} from '@product-web/routes/auth-route'

import config from 'config'

import {EmployeeStatus} from './employee-status'
import {
    forceSessionRefresh,
    getClientSessionId,
    useIsLoggedIn,
    useIsLoggedOut,
    useLogout,
    useShouldRefreshSession,
} from './session'

export type UserData = {
    id: string
    email: string
    role:
        | 'member'
        | 'owner'
        | 'bookkeeper'
        | 'bookkeeper-basic'
        | 'bookkeeper-extended'
        | 'partner-owner'
        | 'partner-member'
    hasPin: boolean
    language: SupportedLanguage | null
    phone?: string | null
    phoneVerified?: boolean | null
    company?: Company
    companies?: Company[] | null
    companyId?: string
    homeCompany?: Company
    intercomHash?: string
    partnerId?: string
    reviewer?: string[]
    departmentManager?: string[]
    status?: {
        verified: boolean
        acceptedTerms: boolean
        pending: boolean
        onboarded: boolean
        readyForExportSwitchDate: string | null
        isSignUpUser?: boolean
    }
    features?: {
        [id: string]: boolean
    }
    partnerEmployee?: {
        id: string
        firstName: string
        lastName: string
        role: 'owner' | 'member'
    }
    partner?: {
        id: string
        name: string
        registrationNumber: string
        address: Address
        ownCompanyId?: string
        tradingName: string
        createdAt: string
    }
    employee?: {
        id: string | null
        firstName: string
        lastName: string
        jobTitle: string | null
        phone: string | null
        email: string
        avatar: {url: string} | null
        status: {state?: Record<string, any>; createdByPartner?: boolean} & Record<string, any>
        company?: Company
        userId: string
        code: null | string
        role: 'member' | 'owner' | 'bookkeeper' | 'bookkeeper-basic' | 'bookkeeper-extended'
        permissions?: EmployeePermission[] | null
    }
    permissions?: CompanyPermissions
    externalPermissions?: ExternalPermissions
    isSpendingEntity?: boolean
    organization?: OrganizationData
    reviewing?: Reviewing
}

export enum ReviewingParentType {
    COMPANY = 'company',
    ORGANIZATION = 'organization',
}

type ReviewingEntry = {
    id: string
    parent: ReviewingParentType
    parentId: string
}

type Reviewing = {
    teams: ReviewingEntry[]
}

export type OrganizationData = {
    id: string
    name: string
}

export type PartnerUserData = UserData & {
    partnerId: string
    partnerEmployee: NonNullable<UserData['partnerEmployee']>
    partner: NonNullable<UserData['partner']>
}

export type CompanyUserData = UserData & {
    company: Company
    companies?: Company[]
    companyId: string
    employee: NonNullable<UserData['employee']>
}

type ExternalPermissions = {
    receipts?: Receipts[]
}

export enum Receipts {
    RECLAIM_VAT = 'reclaimVAT',
}
type UserContextAPI = {
    user?: Readonly<UserData>
    isValidating: boolean
}

////////////////////////////////////////////////////////////////////////////////
// #region User and User Mutations Context

export const UserContext = React.createContext<UserContextAPI>({
    user: undefined,
    isValidating: true,
})

// We are using a separate context for user mutations, to aid testability
// - you won't have to mock all those methods when you component doesn't use mutations
// It's quite rare to need these, as opposed to the user data that almost every page needs
export const UserMutationsContext = React.createContext<ReturnType<typeof getMutations>>({
    forceRefetchUser: async () => Promise.resolve(),
    refetchUser: async () => Promise.resolve(undefined),
    updateAvatar: async () => Promise.resolve(),
    updateProfile: async () => Promise.resolve(),
    updateLanguage: async () => Promise.resolve(),
    setEmployeeStateKey: async () => Promise.resolve(),
    acceptTerms: async () => Promise.resolve(),
})

// This provider wraps the entire app. It's responsible for fetching user's data from the API
// and exposing it to all pages via context.
export const UserProvider: React.FC = ({children}) => {
    // We only want to fire off the request for user when the user is logged in
    const isLoggedIn = useIsLoggedIn()
    const isLoggedOut = useIsLoggedOut()
    const shouldRefreshSession = useShouldRefreshSession()
    const logout = useLogout()

    // If client session exists, it determines which company the user is currently "working for".
    // This applies only to bookkeepers (independent or via a partner)
    const companyId = getClientSessionId()
    const query = companyId ? {companyId} : {}

    // We're not caching by the companyId query parameter on purpose
    // we don't need to keep cache of multiple user info sets
    const {
        data: user,
        error,
        isValidating,
        mutate,
    } = useSWR<UserData, Error>(
        isLoggedIn ? '/rest/v1/user' : null,
        async (route: string) => {
            const [response, authResponse] = await Promise.all([
                request(`${config.endpoints.api}${route}`, {
                    auth: RequestAuth.USER,
                    method: 'GET',
                    query: {
                        ...query,
                        skipEmployeeCreation: true,
                    },
                }),
                request(`${config.endpoints.auth}${route}`, {
                    auth: RequestAuth.USER,
                    method: 'GET',
                }),
            ])

            return {
                ...response,
                status: {...response.status, isSignUpUser: authResponse?.state?.isSignUpUser},
            }
        },
        {shouldRetryOnError: false, revalidateOnFocus: false},
    )

    // Only render the app once we have user data available
    // or the user is not logged in
    // or not session refresh. This last condition is for new otp session handling
    if (!user && !error && !isLoggedOut && !shouldRefreshSession) {
        return <LoadingPage data-testid="loading-indicator" />
    }

    if (!user && error) {
        logout().catch(reportError)
        return null
    }

    const contextValue = {
        isValidating,
        user,
    }

    return (
        <UserContext.Provider value={contextValue}>
            <UserMutationsContext.Provider value={getMutations(query, mutate)}>
                {children}
            </UserMutationsContext.Provider>
        </UserContext.Provider>
    )
}

// #endregion UserContext
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// #region Public methods

// We use this custom error class to be able to recognize errors thrown by custom hooks from this provider
// and ignore them instead of treating as unhandled exceptions and reporting to bug tracker.
// We throw the errors to refine the types returned (so that they are non-nullable) and handle the errors by
// logging users out.
export class UserProviderError extends Error {
    constructor(message?: string) {
        super(message)
        // restore prototype chain
        // see https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
        Object.setPrototypeOf(this, new.target.prototype)

        this.name = UserProviderErrorName
    }
}

// eslint-disable-next-line string-to-lingui/missing-lingui-transformation
export const UserProviderErrorName = 'UserProviderError'

/**
 * Custom hook used by the pages where the user can be logged in or not,
 * to access the user's data (if available) from the context
 * @returns The user object, if available
 */
export function useUser() {
    const {user} = React.useContext(UserContext)
    return user
}

/**
 * Custom hook used by the behind-the-login-screen pages to access the user's data
 * from the context. Note that we are throwing here if the user is not found,
 * so that the returned value is always of non-nullable type.
 * @returns The user object
 */
export function useLoggedInUser() {
    const logout = useLogout()
    const {user} = React.useContext(UserContext)

    if (!user) {
        logout()
        throw new UserProviderError('Not logged in')
    }
    return user
}

/**
 * Custom hook used by the pages accessible to partner employees to access the user's
 * data from the context. Note that we are throwing here if the user is not found, or
 * the partner info is not available, so that the returned value has the type expected
 * of a partner user
 * @returns The user object (always a partner user)
 */
export function usePartnerUser(): PartnerUserData {
    const {user} = React.useContext(UserContext)

    useAllowedRoles(roleSets.partner)

    if (!getIsPartnerUser(user)) {
        window.location.href = '/'
        throw new Error('Not a partner user')
    }
    return user
}

/**
 * Custom hook used by the pages accessible to company employees **only** to access the user's
 * data from the context. Note that we are throwing here if the user is not found, or
 * the company info is not available, so that the returned value has the type expected
 * of a company user. Be careful to not use this hook in pages that are accesible by non-company
 * users, e.g. in 'account/*', where you can favour useLoggedInUser hook instead
 * @returns The user object (always a company user)
 */
export function useCompanyUser(): CompanyUserData {
    const {user} = React.useContext(UserContext)

    useAllowedRoles(roleSets.companyAndReviewer)

    if (!getIsCompanyUser(user)) {
        window.location.href = '/'
        throw new Error('Not a company user')
    }
    return user
}

/**
 * Custom hook used to retrieve if user is the partner user.
 * @returns boolean whether user is a partner user
 */
export function useIsPartnerUser(): boolean {
    const {user} = React.useContext(UserContext)
    return getIsPartnerUser(user)
}

/**
 * Custom hook used to retrieve if user is the company owner. Uses useCompanyUser() so may throw if user is not found.
 * @returns boolean whether user is the company owner
 */
export function useIsCompanyOwner(): boolean {
    const user = useCompanyUser()
    return getIsCompanyOwner(user)
}

/**
 * Custom hook used to retrieve if user is the company bookkeeper. Uses useCompanyUser() so may throw if user is not found.
 * @returns boolean whether user is the company bookkeeper
 */
export function useIsCompanyBookkeeper(): boolean {
    const user = useCompanyUser()
    return getIsBookkeeper(user)
}

/**
 * Custom hook used to retrieve if user is the company extended bookkeeper. Uses useCompanyUser() so may throw if user is not found.
 * @returns boolean whether user is the company extended bookkeeper
 */
export function useIsCompanyExtendedBookkeeper(): boolean {
    const user = useCompanyUser()
    return getIsExtendedBookkeeper(user)
}

/**
 * Custom hook used to retrieve if user is the company basic bookkeeper. Uses useCompanyUser() so may throw if user is not found.
 * @returns boolean whether user is the company basic bookkeeper
 */
export function useIsCompanyBasicBookkeeper(): boolean {
    const user = useCompanyUser()
    return getIsBasicBookkeeper(user)
}

/**
 * Custom hook used to retrieve if user is the team reviewer. Uses useCompanyUser() so may throw if user is not found.
 * @returns boolean whether user is the team reviewer
 */
export function useIsTeamReviewer(): boolean {
    const user = useCompanyUser()
    return getIsReviewer(user)
}

/**
 * Custom hook used to retrieve if user is a regular member. Uses useCompanyUser() so may throw if user is not found.
 * @returns boolean whether user is the company member
 */
export function useIsCompanyMember(): boolean {
    const user = useCompanyUser()
    return getIsMember(user)
}

/**
 * Custom hook used to retrieve if user is assigned to organisations.
 * Uses useCompanyUser() so may throw if user is not found.
 * @returns boolean whether user is an organisation user
 */
function useIsOrganisationUser(): boolean {
    const user = useCompanyUser()
    return getIsOrganizationUser(user)
}

/**
 * Custom hook used to retrieve if user is a reviewer of any organization-teams.
 * Uses useCompanyUser() so may throw if user is not found.
 * @returns the ReviewingEntry if applicable
 */
export function useOrganizationReviewer(): ReviewingEntry | undefined {
    const user = useCompanyUser()

    return getOrganizationReviewer(user)
}

/**
 * Custom hook used to retrieve if user has organisation access.
 * @returns boolean whether user has organisation access
 */
export const useHasOrganisationAccess = () => {
    const hasOrganisationAccess = useIsOrganisationUser()
    return hasOrganisationAccess
}

/**
 * Custom hook used to retrieve if user is in an organisation context.
 * @returns boolean whether user is in an organisation context
 */
export const useIsOrganizationContext = () => {
    const hasOrganisationAccess = useHasOrganisationAccess()
    const isOrganizationReviewer = !!useOrganizationReviewer()

    return hasOrganisationAccess || isOrganizationReviewer
}

/**
 * Custom hook used to retrieve mutation methods that will mutate the user on the server
 * and make sure the cached user available via other custom hooks is updated
 * @returns A dictionary of mutation methods
 */
export function useUserMutations(): ReturnType<typeof getMutations> {
    const mutations = React.useContext(UserMutationsContext)
    return mutations
}

// #endregion Public Methods

////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// #region Mutations
// These functions wrap API calls that mutate data that is returned from the user route
// It runs those calls, optimistically updates the cached user data and triggers a refetch
// of the user data from the API.

// We are wrapping these methods in a function that injected a SWR mutate callback to them
// That way we don't have to worry about the caching key, it's already bound to the mutate method
function getMutations(query: {companyId?: string}, mutate: SWRResponse<UserData, Error>['mutate']) {
    /**
     * Forces a refetch of user's information from the server
     * @returns A promise that resolves when the refetch is complete
     */
    async function refetchUser(): Promise<UserData | undefined> {
        return await mutate()
    }

    /**
     * Force fetch with 'X-Auth-Cache' ignore in order to bypass auth cache when fetching on deimos.
     * Deimos has caching auth for 1 minute by default
     */

    async function forceRefetchUser(queryOverrides?: {companyId?: string}) {
        const response = await request(`${config.endpoints.api}/rest/v1/user`, {
            auth: RequestAuth.USER,
            headers: {
                'X-Auth-Cache': 'ignore',
            },
            method: 'GET',
            query: {
                ...(queryOverrides ? queryOverrides : query),
                skipEmployeeCreation: true,
            },
        })

        await mutate(response, false)
    }

    /**
     * Uploads and updates new avatar for a user, updates the cached user object
     * @param file The uploaded avatar image file (as it's received from HTML file input)
     * @param previewSrc Data encoded URL that can be used to display the new image right away
     * @returns A promise that resolves when the mutation is complete
     */
    async function updateAvatar(file: File, previewSrc: string | null) {
        await apiDeimosUser.updateAvatar(file)

        // We temporarily set the `previewSrc` data URI as the avatar, which gets
        // replaced with the remote file location after the user data is re-fetched by SWR
        const avatar = {url: previewSrc || ''}

        await mutate(
            (cachedUser) =>
                cachedUser && {
                    ...cachedUser,
                    employee: cachedUser?.employee && {
                        ...cachedUser?.employee,
                        avatar,
                    },
                    partnerEmployee: cachedUser?.partnerEmployee && {
                        ...cachedUser?.partnerEmployee,
                        avatar,
                    },
                },
        )
    }

    /**
     * Updates the user's profile information
     * @param viewerRole Conveys what role the user was assuming when performing the profile update
     * @param values Set of updatable profile values, all values need to be provided
     * @returns A promise that resolves when the mutation is complete
     */
    async function updateProfile(
        viewerRole: apiDeimosUser.ViewerRole,
        values: Parameters<typeof apiDeimosUser.updateProfile>[1],
    ) {
        await apiDeimosUser.updateProfile(viewerRole, values)

        await mutate(
            (cachedUser) =>
                cachedUser && {
                    ...cachedUser,
                    employee: cachedUser?.employee && {
                        ...cachedUser?.employee,
                        ...values,
                    },
                    partnerEmployee: cachedUser?.partnerEmployee && {
                        ...cachedUser?.partnerEmployee,
                        ...values,
                    },
                },
        )
    }

    /**
     * Changes the preferred user's UI language
     * @param language The selected preferred language code
     * @returns A promise that resolves when the mutation is complete
     */
    async function updateLanguage(language: SupportedLanguage) {
        await apiAuthUser.setLanguage(language)
        await forceSessionRefresh()
        await forceRefetchUser()
    }

    /**
     * Changes the key-value custom state store for company employees
     * @param key The key to save the information under
     * @param value The value of the saved information
     * @returns A promise that resolves when the mutation is complete
     */
    async function setEmployeeStateKey(key: string, value: string | boolean) {
        await mutate(async (cachedUser) => {
            // You cannot edit state of users who are not company employees
            if (!cachedUser?.employee || !cachedUser.employee.id) {
                return cachedUser
            }
            const state = await apiDeimosEmployee.setState(cachedUser?.employee.id, key, value)

            return {
                ...cachedUser,
                employee: {
                    ...cachedUser?.employee,
                    status: {
                        ...cachedUser?.employee.status,
                        state,
                    },
                },
            }
        })
    }

    /**
     * Saves the information that the user has accepted Pleo's T&C
     * @returns A promise that resolves when the mutation is complete
     */
    async function acceptTerms() {
        await apiAuthUser.acceptTerms()
        await forceSessionRefresh()
        await mutate(
            (cachedUser) =>
                cachedUser && {
                    ...cachedUser,
                    status: cachedUser?.status && {
                        ...cachedUser?.status,
                        acceptedTerms: true,
                    },
                },
        )
    }

    return {
        refetchUser,
        forceRefetchUser,
        updateAvatar,
        updateProfile,
        updateLanguage,
        setEmployeeStateKey,
        acceptTerms,
    }
}

// #endregion Mutations
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// #region Selectors
// These functions allow for an easy access to information commonly derived from
// the user information

export const getIsPartnerOwner = (user?: UserData) => {
    if (user?.partnerEmployee) {
        return user.partnerEmployee.role === 'owner'
    }
    return false
}

export const getHomeCompany = (user: UserData) => {
    // If the user is currently working as a company employee
    // their home company will be the current working company
    if (user.role === 'owner' || user.role === 'member') {
        return user.company
    }

    // Else the user is a bookkeeper or a partner employee
    // their home company is available as `homeCompany`
    // if they have it, else it's just undefined
    return user.homeCompany
}

export const getIsExtendedBookkeeper = (user?: UserData) => {
    return user?.role === 'bookkeeper-extended'
}

export const getIsBasicBookkeeper = (user?: UserData) => {
    return user?.role === 'bookkeeper-basic'
}

export const getIsBookkeeper = (user?: UserData) => {
    return !!user?.role?.startsWith('bookkeeper')
}

export const getIsMember = (user: UserData) => {
    return user.role === 'member'
}

export const getFirstName = (user?: UserData): string => {
    return user?.employee?.firstName ?? user?.partnerEmployee?.firstName ?? ''
}

export const getLastName = (user: UserData): string => {
    return user.employee?.lastName ?? user.partnerEmployee?.lastName ?? ''
}

export const getCountry = (user?: UserData): Country | undefined => {
    const country = user?.company?.address?.country ?? user?.partner?.address?.country
    return country && isCountry(country) ? country : undefined
}

export const getCompanyName = (user: UserData): string | undefined => {
    return user.company?.name
}

export function getCompany(user: CompanyUserData): Company
export function getCompany(user?: UserData): Company | undefined
export function getCompany(user?: UserData): Company | undefined {
    return user?.company
}

export const getLocaleByCountry = (country?: string) => {
    return country ? countryToLocaleMap[country] : undefined
}

/**
 * Get supported browser locale
 */
const getSupportedBrowserLocale = (): Locale | undefined => {
    const browserLocales = window.navigator.languages as Locale[]
    const supportedLocales = Object.values(Locale)
    const browserLocale: Locale | undefined = browserLocales.find((locale) =>
        supportedLocales.includes(locale),
    )
    return browserLocale
}

/**
 * If provided with logged user, it returns language based on user's preferences or their country.
 * With no user, it is defined by supported browser locale or fallbacked to Danish.
 */
export const getLocale = (
    user?: UserData,
    defaultLocale = getSupportedBrowserLocale() || Locale.da_DK,
) => {
    if (user) {
        return (
            getLocaleByLanguage(user.language) ||
            getLocaleByCountry(getCountry(user)) ||
            defaultLocale
        )
    }

    return defaultLocale
}

export const getEmployeeStatus = (user?: UserData) => {
    return user?.employee?.status?.state || {}
}

export const isUserAskedToSwitchTheLanguage = (user?: UserData) => {
    const employeeStatus = getEmployeeStatus(user)
    return Boolean(employeeStatus[EmployeeStatus.askedToSwitchLanguage])
}

export const getFullName = (user: UserData): string => {
    return [getFirstName(user), getLastName(user)].filter((name) => !!name).join(' ')
}

export const hasFeatureFlag = (user: UserData, feature: string) => {
    return !!user?.features?.[feature]
}

export function getIsPartnerUser(user?: UserData): user is PartnerUserData {
    return !!user && !!user.partnerEmployee && !!user.partnerId
}

export function getIsCompanyUser(user?: UserData): user is CompanyUserData {
    return !!user && !!user.employee && !!user.company && !!user.companyId
}

export const getIsCompanyOwner = (user?: UserData): boolean => {
    return user?.role === 'owner'
}

export const getIsReviewer = (user?: UserData): boolean => {
    return !!user?.reviewer?.length
}

export const getHasOwnerAccess = (user: UserData): boolean => {
    return getIsCompanyOwner(user) || getIsBookkeeper(user)
}

export const getIsFdd = (user?: UserData): boolean => {
    return Boolean(user?.company?.status?.fdd)
}

export const getIsOnboardedViaUKB4B = (user?: UserData): boolean => {
    return Boolean(user?.company?.onboardedVia === 'B4B')
}

export function hasGetStarted(user?: UserData) {
    if (!user || user.role === 'owner') {
        return false
    }

    return user?.employee?.status?.state?.getstarted !== 'hide'
}

export function getCompanyPlan(user?: UserData): PlanType | undefined {
    return user?.company?.plan
}

export function getIsEmployeePermitted(permission: EmployeePermission, user?: UserData) {
    if (!user || !user.employee?.permissions?.includes(permission)) {
        return false
    }
    return user.employee.permissions.includes(permission)
}

export function getIsUserPermitted(user: UserData, permission: BookkeeperPermission) {
    return user.permissions && user.permissions[user.companyId || '']?.includes(permission)
}

/**
 * Make properties of type `K` required in an object type `T`.
 */
type MakeRequired<T, K extends keyof T> = Omit<T, K> & Required<Pick<T, K>>

export const getIsOrganizationUser = (
    user?: UserData,
): user is MakeRequired<UserData, 'organization'> => {
    return !!user?.organization
}

export const getOrganizationReviewer = (user?: UserData): ReviewingEntry | undefined => {
    return user?.reviewing?.teams.find(
        (it: ReviewingEntry) => it.parent === ReviewingParentType.ORGANIZATION,
    )
}

export const getIsOrganizationAdmin = (user?: UserData) => {
    if (user?.role !== 'owner') {
        return false
    }
    return !!user?.organization
}

export function useBookkeeperPermitted() {
    const user = useUser()

    const {data, error} = useSWR(user?.companyId || '', checkExternalBookkeepersEntitlement, {
        revalidateOnFocus: false,
    })
    useEffect(() => {
        if (error) {
            reportError(error)
        }
    }, [error])

    if (error) {
        return false
    }
    if (!data) {
        return false
    }

    return data.janusJudgement === 'YES'
}

export type Role =
    | 'member'
    | 'owner'
    | 'bookkeeper-basic'
    | 'bookkeeper-extended'
    | 'reviewer'
    | 'partner-owner'
    | 'partner-member'

export const roleSets = {
    company: ['member', 'owner', 'bookkeeper-basic', 'bookkeeper-extended'],
    companyAndReviewer: ['reviewer', 'member', 'owner', 'bookkeeper-basic', 'bookkeeper-extended'],
    companyOnly: ['member', 'owner'],
    ownerAndBookkeeper: ['owner', 'bookkeeper-basic', 'bookkeeper-extended'],
    ownerAndBookkeeperExtended: ['owner', 'bookkeeper-extended'],
    partner: ['partner-member', 'partner-owner'],
    all: [
        'reviewer',
        'member',
        'owner',
        'bookkeeper-basic',
        'bookkeeper-extended',
        'partner-member',
        'partner-owner',
    ],
} satisfies Record<string, Role[]>

// Checks if the logged in user has any of the allowed roles,
// or a role with broader permission than one of the allowed roles
export const useHasAllowedRole = (allowedRoles?: Role[]) => {
    const user = useUser()
    const userRoles: Role[] = []

    invariant(allowedRoles?.length, `Allowed roles can't be empty under an auth route.`)

    // if no user is logged in, the condition can't be met
    if (!user) {
        return false
    }

    // unfolding hierarchical roles, so e.g. owner has access to routes that require member
    if (user?.role === 'owner') {
        userRoles.push('owner', 'member')
    } else if (user?.role === 'member') {
        userRoles.push('member')
    } else if (user?.role === 'bookkeeper-extended') {
        userRoles.push('bookkeeper-extended', 'bookkeeper-basic')
    } else if (user?.role === 'bookkeeper' || user?.role === 'bookkeeper-basic') {
        userRoles.push('bookkeeper-basic')
    }

    if (user?.partnerEmployee?.role === 'owner') {
        userRoles.push('partner-owner', 'partner-member')
    } else if (user?.partnerEmployee?.role === 'member') {
        userRoles.push('partner-member')
    }

    // treating reviewer as a role here, even tough it's technically not, for convenience
    if (user?.reviewer?.length) {
        userRoles.push('reviewer')
    }

    return !!intersection(allowedRoles, userRoles).length
}

// #endregion Selectors
////////////////////////////////////////////////////////////////////////////////
